import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardMedia,
  Typography,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  Chip,
  CircularProgress,
  Alert,
  useTheme,
  useMediaQuery,
  Divider,
  Switch,
  FormControlLabel,
  InputAdornment,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  Upload,
  Save,
  Cancel,
  Close,
  PhotoCamera,
  DragIndicator,
  Visibility,
  VisibilityOff,
} from '@mui/icons-material';
import { useNotification } from '../../contexts/NotificationContext';
import api from '../../services/api';
import { buildImageUrl } from '../../utils/imageUtils';

const SliderManagement = () => {
  const [sliderImages, setSliderImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingImage, setEditingImage] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    subtitle: '',
    button_text: '',
    button_link: '',
    sort_order: 0,
    is_active: true,
  });
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);

  const { showSuccess, showError } = useNotification();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  useEffect(() => {
    loadSliderImages();
  }, []);

  const loadSliderImages = async () => {
    try {
      setLoading(true);
      const response = await api.get('/slider-images/');
      setSliderImages(response.data);
    } catch (error) {
      console.error('Error loading slider images:', error);
      showError('Failed to load slider images');
    } finally {
      setLoading(false);
    }
  };

  const handleAddNew = () => {
    setEditingImage(null);
    setFormData({
      title: '',
      subtitle: '',
      button_text: '',
      button_link: '',
      sort_order: sliderImages.length,
      is_active: true,
    });
    setImageFile(null);
    setImagePreview(null);
    setDialogOpen(true);
  };

  const handleEdit = (image) => {
    setEditingImage(image);
    setFormData({
      title: image.title || '',
      subtitle: image.subtitle || '',
      button_text: image.button_text || '',
      button_link: image.button_link || '',
      sort_order: image.sort_order || 0,
      is_active: image.is_active,
    });
    setImageFile(null);
    setImagePreview(buildImageUrl(image.image_url));
    setDialogOpen(true);
  };

  const handleImageChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      setImageFile(file);
      const reader = new FileReader();
      reader.onload = (e) => setImagePreview(e.target.result);
      reader.readAsDataURL(file);
    }
  };

  const handleSave = async () => {
    try {
      setUploading(true);
      
      const formDataToSend = new FormData();
      formDataToSend.append('restaurant_id', '1'); // Assuming restaurant ID is 1
      formDataToSend.append('title', formData.title);
      formDataToSend.append('subtitle', formData.subtitle);
      formDataToSend.append('button_text', formData.button_text);
      formDataToSend.append('button_link', formData.button_link);
      formDataToSend.append('sort_order', formData.sort_order.toString());
      formDataToSend.append('is_active', formData.is_active.toString());
      
      if (imageFile) {
        formDataToSend.append('image', imageFile);
      }

      if (editingImage) {
        // Update existing image
        await api.put(`/slider-images/${editingImage.id}`, formDataToSend, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        showSuccess('Slider image updated successfully!');
      } else {
        // Create new image
        await api.post('/slider-images/', formDataToSend, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        showSuccess('Slider image added successfully!');
      }

      setDialogOpen(false);
      loadSliderImages();
    } catch (error) {
      console.error('Error saving slider image:', error);
      showError('Failed to save slider image');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (imageId) => {
    if (window.confirm('Are you sure you want to delete this slider image?')) {
      try {
        await api.delete(`/slider-images/${imageId}`);
        showSuccess('Slider image deleted successfully!');
        loadSliderImages();
      } catch (error) {
        console.error('Error deleting slider image:', error);
        showError('Failed to delete slider image');
      }
    }
  };

  const handleToggleActive = async (imageId, isActive) => {
    try {
      await api.put(`/slider-images/${imageId}`, {
        is_active: !isActive
      }, {
        headers: { 'Content-Type': 'application/json' }
      });
      showSuccess('Slider image status updated!');
      loadSliderImages();
    } catch (error) {
      console.error('Error updating slider image status:', error);
      showError('Failed to update slider image status');
    }
  };

  const handleSortOrderChange = async (imageId, newSortOrder) => {
    try {
      await api.put(`/slider-images/${imageId}`, {
        sort_order: newSortOrder
      }, {
        headers: { 'Content-Type': 'application/json' }
      });
      loadSliderImages();
    } catch (error) {
      console.error('Error updating sort order:', error);
      showError('Failed to update sort order');
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" py={4}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Add New Button */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6" fontWeight="600">
          Slider Images ({sliderImages.length})
        </Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={handleAddNew}
          size={isMobile ? "small" : "medium"}
        >
          Add New Image
        </Button>
      </Box>

      {/* Slider Images Grid */}
      {sliderImages.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <PhotoCamera sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No slider images yet
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Add your first slider image to create an engaging homepage hero section
          </Typography>
          <Button variant="contained" startIcon={<Add />} onClick={handleAddNew}>
            Add First Image
          </Button>
        </Box>
      ) : (
        <Grid container spacing={2}>
          {sliderImages.map((image, index) => (
            <Grid item xs={12} sm={6} md={4} key={image.id}>
              <Card sx={{ position: 'relative', height: 300 }}>
                <CardMedia
                  component="img"
                  height="200"
                  image={buildImageUrl(image.image_url)}
                  alt={image.title || 'Slider Image'}
                  sx={{ objectFit: 'cover' }}
                />
                <CardContent sx={{ p: 2, height: 100, overflow: 'hidden' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                    <Typography variant="subtitle2" fontWeight="600" noWrap sx={{ flex: 1 }}>
                      {image.title || 'Untitled'}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 0.5, ml: 1 }}>
                      <Chip
                        label={image.is_active ? 'Active' : 'Inactive'}
                        size="small"
                        color={image.is_active ? 'success' : 'default'}
                        variant="outlined"
                      />
                    </Box>
                  </Box>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                    Order: {image.sort_order}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ 
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}>
                    {image.subtitle || 'No subtitle'}
                  </Typography>
                </CardContent>
                
                {/* Action Buttons */}
                <Box sx={{ 
                  position: 'absolute', 
                  top: 8, 
                  right: 8, 
                  display: 'flex', 
                  gap: 0.5,
                  opacity: 0.8,
                  '&:hover': { opacity: 1 }
                }}>
                  <IconButton
                    size="small"
                    onClick={() => handleToggleActive(image.id, image.is_active)}
                    sx={{ 
                      bgcolor: 'background.paper', 
                      '&:hover': { bgcolor: 'background.paper' }
                    }}
                  >
                    {image.is_active ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => handleEdit(image)}
                    sx={{ 
                      bgcolor: 'background.paper', 
                      '&:hover': { bgcolor: 'background.paper' }
                    }}
                  >
                    <Edit />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => handleDelete(image.id)}
                    sx={{ 
                      bgcolor: 'background.paper', 
                      '&:hover': { bgcolor: 'background.paper' }
                    }}
                  >
                    <Delete />
                  </IconButton>
                </Box>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Add/Edit Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle>
          {editingImage ? 'Edit Slider Image' : 'Add New Slider Image'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={3} sx={{ mt: 1 }}>
            {/* Image Upload */}
            <Grid item xs={12}>
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Image *
                </Typography>
                <Box sx={{ 
                  border: '2px dashed', 
                  borderColor: 'divider', 
                  borderRadius: 2, 
                  p: 3, 
                  textAlign: 'center',
                  cursor: 'pointer',
                  '&:hover': { borderColor: 'primary.main' }
                }}>
                  {imagePreview ? (
                    <Box>
                      <img
                        src={imagePreview}
                        alt="Preview"
                        style={{
                          maxWidth: '100%',
                          maxHeight: 200,
                          objectFit: 'cover',
                          borderRadius: 8
                        }}
                      />
                      <Button
                        variant="outlined"
                        startIcon={<Upload />}
                        onClick={() => document.getElementById('image-upload').click()}
                        sx={{ mt: 2 }}
                      >
                        Change Image
                      </Button>
                    </Box>
                  ) : (
                    <Box>
                      <PhotoCamera sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        Click to upload or drag and drop
                      </Typography>
                      <Button
                        variant="outlined"
                        startIcon={<Upload />}
                        onClick={() => document.getElementById('image-upload').click()}
                      >
                        Choose Image
                      </Button>
                    </Box>
                  )}
                  <input
                    id="image-upload"
                    type="file"
                    accept="image/*"
                    onChange={handleImageChange}
                    style={{ display: 'none' }}
                  />
                </Box>
              </Box>
            </Grid>

            {/* Form Fields */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Title"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="Enter image title"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Sort Order"
                type="number"
                value={formData.sort_order}
                onChange={(e) => setFormData({ ...formData, sort_order: parseInt(e.target.value) || 0 })}
                placeholder="Display order"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Subtitle"
                value={formData.subtitle}
                onChange={(e) => setFormData({ ...formData, subtitle: e.target.value })}
                placeholder="Enter image subtitle"
                multiline
                rows={2}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Button Text"
                value={formData.button_text}
                onChange={(e) => setFormData({ ...formData, button_text: e.target.value })}
                placeholder="e.g., Order Now"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Button Link"
                value={formData.button_link}
                onChange={(e) => setFormData({ ...formData, button_link: e.target.value })}
                placeholder="e.g., /delivery"
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.is_active}
                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                  />
                }
                label="Active (visible on website)"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} disabled={uploading}>
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={uploading || (!imageFile && !editingImage)}
            startIcon={uploading ? <CircularProgress size={20} /> : <Save />}
          >
            {uploading ? 'Saving...' : (editingImage ? 'Update' : 'Add')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SliderManagement;
